import React from 'react';

interface PixelGridProps {
  pixels: number[][];
  colors: string[];
  pixelSize: number;
  onPixelClick(x: number, y: number): void;
  onPointerDown?(e: React.PointerEvent): void;
  onPointerMove?(e: React.PointerEvent): void;
  onPointerUp?(e: React.PointerEvent): void;
  overlay?: React.ReactNode; // NYTT: renderas som sista barn i .pixel-grid
  // NYTT: sekundär palett för index 128..143 (och ev. -16..-1)
  secondaryColors?: string[];
}

const PixelGrid: React.FC<PixelGridProps> = ({
  pixels,
  colors,
  pixelSize,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  overlay,
  secondaryColors,
}) => {
  const size = pixels.length; // 8 eller 16

  // Viktigt: floor i stället för round så vi inte överskjuter 1 px på vissa DPR
  const adjustedPixelSize = Math.max(1, Math.floor((pixelSize * 16) / size));

  const resolveColor = (ci: number): string => {
    // Stöd två kodningar för secondary:
    // 1) 128..143 (PICO‑8-stil)   → (ci & 0x80) != 0
    // 2) -16..-1 (alternativ)     → ci < 0
    const isSecondary = (ci & 0x80) !== 0 || ci < 0;
    const baseIndex = ci < 0 ? (ci + 16) & 0x0f : ci & 0x0f;

    if (isSecondary) {
      const sec = secondaryColors ?? colors;
      return sec[baseIndex] ?? colors[baseIndex] ?? 'transparent';
    }
    return colors[baseIndex] ?? 'transparent';
  };

  return (
    <div
      className="pixel-grid-container"
      style={{ ['--pixel-size' as any]: `${adjustedPixelSize}px`, ['--grid-size' as any]: size }}
    >
      <div
        className="pixel-grid"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {pixels.map((row, y) => (
          <div key={y} className="pixel-row">
            {row.map((ci, x) => (
              <div
                key={x}
                className="pixel pixel-editable"
                data-x={x}
                data-y={y}
                style={{ backgroundColor: resolveColor(ci) }}
              />
            ))}
          </div>
        ))}
        {overlay}
      </div>
    </div>
  );
};

export default PixelGrid;